import React, { Component, Fragment } from 'react';
import { Button, Elevation, Typography, Icon } from 'rmwc';
import {
  Card,
  CardPrimary,
  CardTitle,
  CardSubtitle,
  CardHorizontalBlock,
  CardSupportingText,
  CardMediaItem,
} from 'rmwc/Card';
import { Text } from '../../components/Text';
import { CTAOptDense } from '../../components/Onboarding/CtaVariants';
import { ExpandableGroup, ExpandableItem } from '../../components/Expandable';
import { PlanCTA } from '../Onboarding/PlanIntro';

import styled from 'styled-components';
import { media, screenSizes, Container } from '../../helpers/styleConstants';

const borderColor = '#e0e0e0';

export const MTHeader = ({ title, onClick }) => (
  <div className="clearfix" style={{ padding: '0.5rem 0 1rem' }}>
    {onClick && (
      <div style={{ float: 'right' }}>
        <Button theme="secondary" onClick={onClick}>
          Back to Overview
        </Button>
      </div>
    )}

    <Text style={{ float: 'left' }} use="headline5" className="mt-1 mb-0">
      {title}
    </Text>

    <div style={{ clear: 'both' }} />

    <div
      className="d-none pt-3 mb-3"
      style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
    >
      <Text use="headline5" className="mt-1 mb-0">
        {title}
      </Text>
      <div style={{ display: 'inline-block' }}>
        {onClick && (
          <Button theme="secondary" onClick={onClick}>
            Back to Overview
          </Button>
        )}
      </div>
    </div>
  </div>
);

export const MTDivider = () => <hr style={{ margin: '1rem -15px', border: 'none', borderTop: '1px solid #e0e0e0' }} />;

export const MTSkinny = styled.div`
  width: 100%;
  ${media.tabletLandscapeUp`
    width: 100%;
  `};

  ${media.desktopUp`
    width: 75%;
  `};

  ${media.bigDesktopUp`
    width: 665px;
  `};
`;

export const MTWide = ({ children }) => children;

export const MTCalc = ({ title, left, right, leftWidth = 8, rightWidth = 4, disclaimer = false }) => (
  <Fragment>
    <Elevation
      className={disclaimer ? 'mb-1' : 'mb-3'}
      style={{ backgroundColor: 'white', padding: '1rem 15px', margin: '0 0' }}
      z={2}
    >
      <div className="row">
        <div className={`col-${leftWidth}`}>{left}</div>
        <div
          className={`col-${rightWidth}`}
          style={{
            backgroundColor: '#FAFAFA',
            borderLeft: '1px solid #e0e0e0',
            margin: '-1rem 0',
            padding: '1rem 15px',
          }}
        >
          {right}
        </div>
      </div>
    </Elevation>
    {disclaimer && (
      <MTSkinny>
        <Typography className="mb-3" tag="div" use="caption" theme="text-hint-on-background">
          This calculator is provided for informational purposes only. It is not intended to provide personal investment
          advice and does not take into account the investment objectives and financial resources of the reader.
        </Typography>
      </MTSkinny>
    )}
  </Fragment>
);

export const MTSubheading = ({ title }) => (
  <Text use="headline6" className="mt-0 mb-2" tag="div">
    {title}
  </Text>
);

export const MTSubcontentHR = ({ style }) => (
  <hr className="hrbr-rule" style={Object.assign({ left: '0', width: 'auto', margin: '1.5rem -1rem' }, style)} />
);

export const MTSubcontentH1 = ({ title }) => (
  <Text use="headline6" className="mt-0 mb-1" tag="div">
    {title}
  </Text>
);

export const MTSubcontentH2 = ({ title }) => (
  <Text use="subtitle1" className="mt-4 mb-2" tag="div">
    <strong>{title}</strong>
  </Text>
);

export const MTSubcontentLabel = ({ children }) => (
  <Text use="body1" adjustMargin={false} className="mb-1" tag="div">
    {children}
  </Text>
);

export const MTSubcontent = ({ children, noCard }) =>
  noCard ? (
    <div className="mb-2">{children}</div>
  ) : (
    <Elevation className="mb-3" style={{ backgroundColor: 'white', padding: '1rem 1rem 0', margin: '0 0' }} z={2}>
      {children}
    </Elevation>
  );

export const MTSubcontentP = ({ children, className, last }) => (
  <Text
    className={className}
    use="subtitle1"
    style={{ lineHeight: '24px', margin: 0, paddingBottom: last ? 0 : '1rem' }}
  >
    {children}
  </Text>
);

export const MTSubcontentActions = ({ children }) => (
  <Text use="subtitle1" style={{ lineHeight: '24px', margin: 0, paddingBottom: '1rem' }}>
    {children}
  </Text>
);

export const MTScorePanel = ({ score1_title, score1, score2_title, score2, score3_title, score3 }) => (
  <Fragment>
    <div style={{ display: 'flex', alignItems: 'center', textAlign: 'center' }}>
      <div style={{ flexBasis: 0, flexGrow: 1, padding: '0 32px' }}>
        <Typography use="body1">{score1_title}</Typography>
      </div>
      <div style={{ flexBasis: 0, flexGrow: 1, padding: '0 32px' }}>
        <Typography use="body1">{score2_title}</Typography>
      </div>
      <div style={{ flexBasis: 0, flexGrow: 1, padding: '0 32px' }}>
        <Typography use="body1">{score3_title}</Typography>
      </div>
    </div>
    <div style={{ display: 'flex', alignItems: 'center', textAlign: 'center' }}>
      <div style={{ flexBasis: 0, flexGrow: 1, padding: '0 32px' }}>{score1}</div>
      <div style={{ flexBasis: 0, flexGrow: 1, padding: '0 32px' }}>{score2}</div>
      <div style={{ flexBasis: 0, flexGrow: 1, padding: '0 32px' }}>{score3}</div>
    </div>
  </Fragment>
);

export const MTCTA = ({
  title,
  subtitle,
  description,
  image,
  buttonTextPrimary,
  onClickPrimary,
  buttonTextSecondary,
  onClickSecondary,
  talkingPointsHeading,
  talkingPoints,
}) => (
  <Fragment>
    <CTAOptDense
      style={{ width: 'auto', margin: '0 0' }}
      title={title}
      subtitle={subtitle}
      image={image}
      description={description}
      buttonTextPrimary={buttonTextPrimary}
      onClickPrimary={onClickPrimary}
      buttonTextSecondary={buttonTextSecondary}
      onClickSecondary={onClickSecondary}
    />
    <Text use="subtitle1" className="mt-4 mb-1">
      {talkingPointsHeading}
    </Text>
    <div style={{ borderTop: `1px solid ${borderColor}`, margin: '0 -1rem' }}>
      {talkingPoints.map(x => (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            borderBottom: `1px solid ${borderColor}`,
            padding: '0.5rem 1rem',
          }}
        >
          <img src={x.image} style={{ height: '30px', marginRight: '0.5rem' }} />
          <Typography use="subtitle1">
            <strong>{x.label}</strong>
          </Typography>
        </div>
      ))}
    </div>
  </Fragment>
);

class MTCTAv2Expandable extends Component {
  constructor(props) {
    super(props);
    this.state = { open: false };
  }

  onClick = () => {
    this.setState({ open: !this.state.open });
  };

  render() {
    return (
      <div style={{ borderBottom: `1px solid ${borderColor}` }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            cursor: 'pointer',
          }}
          onClick={this.onClick}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              padding: '0.5rem 1rem',
            }}
          >
            <img src={this.props.option.image} style={{ height: '30px', marginRight: '0.5rem' }} />
            <Typography use="subtitle1">
              <strong>{this.props.option.label}</strong>
            </Typography>
          </div>
          <Icon className="mr-3" style={{}} theme="text-secondary-on-background">
            {this.state.open ? 'expand_less' : 'add'}
          </Icon>
        </div>
        {this.state.open && this.props.option.text}
      </div>
    );
  }
}

export const MTCTAv3 = () => <PlanCTA />;

export const MTCTAv2 = ({
  title,
  subtitle,
  description,
  image,
  buttonTextPrimary,
  onClickPrimary,
  buttonTextSecondary,
  onClickSecondary,
  talkingPointsHeading,
  talkingPointsSubheading,
  talkingPointsImage,
  talkingPoints
}) => (
  <Fragment>
    <Card
      style={{ boxShadow: 'none', border: '1px solid #dbdbdb', width: 'auto', margin: '0 0', padding: '1rem' }}
      className="mb-4"
    >
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <div style={{ marginLeft: 0 }}>
          <Typography tag="div" use="headline6">
            {talkingPointsHeading}
          </Typography>
          <Typography tag="div" use="body1" theme="text-secondary-on-background">
            {talkingPointsSubheading}
          </Typography>
        </div>
        <div style={{ width: '70px', marginLeft: '1rem' }}>
          <img src={talkingPointsImage} style={{ height: '70px' }} />
        </div>
      </div>
      <div className="pb-0 mt-2">
        <div style={{ borderTop: `1px solid ${borderColor}`, margin: '0 -1rem' }}>
          {talkingPoints.map(x => <MTCTAv2Expandable option={x} key={x.label} />)}
        </div>
      </div>
    </Card>

    <PlanCTA />
  </Fragment>
);

export const MTAssumptionsSection = ({ children, title, titleStyle }) => (
  <div>
    <Typography tag="div" use={titleStyle}>
      <strong>{title}</strong>
    </Typography>
    <div>{children}</div>
  </div>
);

export const MTLearnMore = ({ ...props }) => (
  <ExpandableGroup
    style={{ borderTop: '1px solid #EEE', margin: '0 0' }}
    openIndex={0}
    titleStyle="subheading2"
    {...props}
  />
);

export const MTLearnMoreItem = ({ title, children, open, ...props }) => (
  <ExpandableItem
    style={{ padding: '0.5rem 1rem' }}
    contentStyle={{ padding: '1rem 1rem 0', backgroundColor: 'rgb(224, 243, 255)', display: 'flex' }}
    title={open ? <strong>{title}</strong> : title}
    open={open}
    {...props}
  >
    <div>{children}</div>
  </ExpandableItem>
);
